import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { OrganizationService } from '../../core/data-services/organization.service';
import { StateReportService } from '../../core/data-services/state-report.service';
import { StateReport } from '../../core/data-models/state-report.model';
import { StateReportWithJobs } from '../../core/response-models/state-report-with-jobs.model';
import { StateReportStatus } from '../../core/data-models/state-report-status.model';
import { StateReportType } from '../../core/data-models/state-report-type.model';
import { StateReportQueueStats } from '../../core/response-models/state-report-queue-stats.model';
import { StateReportOptions } from '../../core/data-models/state-report-options.model';
import { StateReportFilters } from '../../core/request-models/state-report-filters.model';
import { OrganizationOption } from '../../core/response-models/organization-option.model';
import { US_STATES } from '../../shared/constants/us-states';
import { UsStateSelect } from '../../shared/components/app-us-state-select/app-us-state-select';
import { openExternal } from '../../core/utils/external-link-utils';

/**
 * State Reports Admin Page
 *
 * Displays all state compliance reports across organizations with filtering,
 * queue statistics, and management actions (view, delete, regenerate, retry).
 * Includes batch generation functionality for generating reports for multiple
 * organizations at once.
 */
@Component({
  selector: 'app-state-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, UsStateSelect],
  templateUrl: './state-reports.component.html',
  styleUrl: './state-reports.component.scss'
})
export class StateReportsComponent implements OnInit, OnDestroy {
  reports: StateReport[] = [];
  organizations: OrganizationOption[] = [];
  queueStats: StateReportQueueStats | null = null;
  loading = true;
  fetchingQueueStats = true;
  errorMessage = '';
  successMessage = '';

  // Polling for generating reports
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly POLLING_INTERVAL_MS = 3000; // Poll every 3 seconds when generating

  // Pagination
  currentPage = 1;
  pageSize = 20;
  totalReports = 0;
  totalPages = 0;

  // Filters
  searchQuery = '';
  filterStatus: 'all' | StateReportStatus = 'all';
  filterOrganization = '';
  filterState = '';
  filterStartDate = '';
  filterEndDate = '';

  // Modal state
  selectedReport: StateReportWithJobs | null = null;
  detailModalVisible = false;
  deleteModalVisible = false;
  regenerateModalVisible = false;
  reportToDelete: StateReport | null = null;
  reportToRegenerate: StateReport | null = null;
  actionLoading = false;

  // Status options for filter
  statusOptions: { value: string; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'generating', label: 'Generating' },
    { value: 'ready', label: 'Ready' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'failed', label: 'Failed' }
  ];

  // ============================================
  // BATCH GENERATION STATE
  // ============================================

  batchSectionExpanded = false;
  selectedOrganizations = new Set<string>();
  organizationStatesCache = new Map<string, string[]>();
  batchStartDate = '';
  batchEndDate = '';
  batchReportType: StateReportType = 'all';
  batchOrganizationFilter = '';
  batchGenerating = false;
  batchConfirmModalVisible = false;
  loadingOrgStates = new Set<string>();

  // Default batch report options
  batchOptions: StateReportOptions = {
    includeChemicalDetails: true,
    includeApplicationRates: true,
    includeFieldMaps: true,
    includeWeatherConditions: true
  };

  // Report type options for batch generation
  reportTypeOptions: { value: StateReportType; label: string }[] = [
    { value: 'all', label: 'All Types' },
    { value: 'pesticide', label: 'Pesticide' },
    { value: 'fertilizer', label: 'Fertilizer' },
    { value: 'seeding', label: 'Seeding' },
    { value: 'other', label: 'Other' }
  ];

  // Batch limit warning threshold
  readonly BATCH_LIMIT_WARNING = 50;

  constructor(
    private stateReportService: StateReportService,
    private organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.initializeBatchDates();
    this.loadOrganizations();
    this.loadQueueStats();
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ============================================
  // BATCH GENERATION METHODS
  // ============================================

  /**
   * Initialize default date range for batch generation (last 30 days)
   */
  private initializeBatchDates(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    this.batchEndDate = this.formatDateForInput(today);
    this.batchStartDate = this.formatDateForInput(thirtyDaysAgo);
  }

  /**
   * Format date for HTML date input (YYYY-MM-DD)
   */
  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Toggle batch generation section visibility
   */
  toggleBatchSection(): void {
    this.batchSectionExpanded = !this.batchSectionExpanded;
  }

  /**
   * Toggle organization selection and fetch states if selecting
   */
  async toggleOrganizationSelection(orgId: string): Promise<void> {
    if (this.selectedOrganizations.has(orgId)) {
      this.selectedOrganizations.delete(orgId);
    } else {
      this.selectedOrganizations.add(orgId);
      await this.fetchOrganizationStates(orgId);
    }
  }

  /**
   * Fetch states for an organization (with caching)
   */
  async fetchOrganizationStates(orgId: string): Promise<void> {
    // Skip if already cached
    if (this.organizationStatesCache.has(orgId)) {
      return;
    }

    // Skip if already loading
    if (this.loadingOrgStates.has(orgId)) {
      return;
    }

    this.loadingOrgStates.add(orgId);

    try {
      const states = await firstValueFrom(this.organizationService.getReportStates(orgId));
      this.organizationStatesCache.set(orgId, states);
    } catch (error) {
      console.error(`Failed to fetch states for organization ${orgId}:`, error);
      this.organizationStatesCache.set(orgId, []);
    } finally {
      this.loadingOrgStates.delete(orgId);
    }
  }

  /**
   * Get states for an organization from cache
   */
  getOrganizationStatesFromCache(orgId: string): string[] {
    return this.organizationStatesCache.get(orgId) || [];
  }

  /**
   * Check if states are loading for an organization
   */
  isLoadingOrgStates(orgId: string): boolean {
    return this.loadingOrgStates.has(orgId);
  }

  /**
   * Get filtered organizations based on search input
   */
  get filteredBatchOrganizations(): OrganizationOption[] {
    if (!this.batchOrganizationFilter.trim()) {
      return this.organizations;
    }
    const filter = this.batchOrganizationFilter.toLowerCase().trim();
    return this.organizations.filter(org =>
      org.name.toLowerCase().includes(filter)
    );
  }

  /**
   * Select all currently filtered organizations
   */
  async selectAllFiltered(): Promise<void> {
    const filtered = this.filteredBatchOrganizations;
    const newSelections: string[] = [];

    for (const org of filtered) {
      if (!this.selectedOrganizations.has(org.id)) {
        this.selectedOrganizations.add(org.id);
        newSelections.push(org.id);
      }
    }

    // Fetch states for newly selected organizations in parallel
    await Promise.all(
      newSelections.map(orgId => this.fetchOrganizationStates(orgId))
    );
  }

  /**
   * Clear all organization selections and cached states
   */
  clearSelection(): void {
    this.selectedOrganizations.clear();
    this.organizationStatesCache.clear();
  }

  /**
   * Check if an organization is selected
   */
  isOrganizationSelected(orgId: string): boolean {
    return this.selectedOrganizations.has(orgId);
  }

  /**
   * Get total report count (sum of states across all selected orgs)
   */
  getTotalReportCount(): number {
    let total = 0;
    for (const orgId of this.selectedOrganizations) {
      const states = this.organizationStatesCache.get(orgId);
      if (states && states.length > 0) {
        total += states.length;
      }
    }
    return total;
  }

  /**
   * Get unique state count across all selected organizations
   */
  getUniqueStateCount(): number {
    const uniqueStates = new Set<string>();
    for (const orgId of this.selectedOrganizations) {
      const states = this.organizationStatesCache.get(orgId);
      if (states) {
        states.forEach(state => uniqueStates.add(state));
      }
    }
    return uniqueStates.size;
  }

  /**
   * Check if batch date range is valid
   */
  isBatchDateRangeValid(): boolean {
    if (!this.batchStartDate || !this.batchEndDate) {
      return false;
    }
    return this.batchStartDate <= this.batchEndDate;
  }

  /**
   * Check if batch generation can be submitted
   */
  canGenerateBatch(): boolean {
    return (
      this.selectedOrganizations.size > 0 &&
      this.isBatchDateRangeValid() &&
      !this.batchGenerating
    );
  }

  /**
   * Show batch limit warning if needed
   */
  get showBatchLimitWarning(): boolean {
    return this.selectedOrganizations.size > this.BATCH_LIMIT_WARNING;
  }

  /**
   * Open batch confirmation modal
   */
  confirmBatchGeneration(): void {
    if (!this.canGenerateBatch()) {
      return;
    }
    this.batchConfirmModalVisible = true;
    this.openModal('batchConfirmModal');
  }

  /**
   * Cancel batch confirmation
   */
  cancelBatchConfirmation(): void {
    this.batchConfirmModalVisible = false;
    this.closeModal('batchConfirmModal');
  }

  /**
   * Execute batch generation
   */
  async generateBatch(): Promise<void> {
    if (!this.canGenerateBatch()) {
      return;
    }

    this.batchGenerating = true;
    this.errorMessage = '';

    try {
      const organizationIds = Array.from(this.selectedOrganizations);

      const result = await this.stateReportService.generateBatch(
        organizationIds,
        this.batchStartDate,
        this.batchEndDate,
        this.batchReportType,
        this.batchOptions
      );

      // Close confirmation modal
      this.cancelBatchConfirmation();

      // Show success message
      const reportCount = result.created;
      this.successMessage = `Successfully queued ${reportCount} report${reportCount !== 1 ? 's' : ''} for generation`;
      this.clearSuccessMessage();

      // Collapse batch section and clear selections
      this.batchSectionExpanded = false;
      this.clearSelection();

      // Refresh reports table
      await this.loadReports();
      await this.loadQueueStats();

    } catch (error: any) {
      console.error('Batch generation error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to generate batch reports';
    } finally {
      this.batchGenerating = false;
    }
  }

  /**
   * Get report type label for display
   */
  getBatchReportTypeLabel(): string {
    const option = this.reportTypeOptions.find(opt => opt.value === this.batchReportType);
    return option?.label || this.batchReportType;
  }

  // ============================================
  // POLLING FOR GENERATING REPORTS
  // ============================================

  private startPolling(): void {
    if (this.pollingInterval) return; // Already polling

    this.pollingInterval = setInterval(async () => {
      // Silently refresh data without showing loading indicator
      try {
        await this.fetchReports();

        // Also refresh queue stats
        this.queueStats = await this.stateReportService.getQueueStats();

        // Check if we should continue polling
        this.checkAndUpdatePolling();
      } catch (error) {
        console.error('Polling refresh error:', error);
      }
    }, this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private checkAndUpdatePolling(): void {
    const hasGenerating = this.reports.some(r => r.status === 'generating') ||
                          (this.queueStats?.active ?? 0) > 0 ||
                          (this.queueStats?.pending ?? 0) > 0;

    if (hasGenerating && !this.pollingInterval) {
      this.startPolling();
    } else if (!hasGenerating && this.pollingInterval) {
      this.stopPolling();
    }
  }

  // ============================================
  // DATA LOADING
  // ============================================

  async loadOrganizations(): Promise<void> {
    try {
      const page = await firstValueFrom(this.organizationService.getOptions());
      this.organizations = page.items;
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
    }
  }

  async loadQueueStats(): Promise<void> {
    this.fetchingQueueStats = true;
    try {
      this.queueStats = await this.stateReportService.getQueueStats();
    } catch (error: any) {
      console.error('Failed to load queue stats:', error);
    } finally {
      this.fetchingQueueStats = false;
    }
  }

  async loadReports(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      await this.fetchReports();

      // Start/stop polling based on whether there are generating reports
      this.checkAndUpdatePolling();
    } catch (error: any) {
      console.error('Load reports error:', error);
      this.errorMessage = 'Failed to load reports';
    } finally {
      this.loading = false;
    }
  }

  /** Loads the current page with the current filters into `reports` and the paging totals. */
  private async fetchReports(): Promise<void> {
    const filters: StateReportFilters = {
      page: this.currentPage,
      pageSize: this.pageSize,
      status: this.filterStatus !== 'all' ? this.filterStatus : undefined,
      organizationId: this.filterOrganization,
      state: this.filterState,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
      search: this.searchQuery,
    };
    const page = await this.stateReportService.listReports(filters);
    this.reports = page.items;
    this.totalReports = page.paging.totalItems;
    this.totalPages = page.paging.totalPages;
  }

  // ============================================
  // FILTER HANDLERS
  // ============================================

  onSearch(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadReports();
  }

  showClearFilters(): boolean {
    return !!(this.searchQuery || this.filterOrganization || this.filterState || this.filterStatus !== 'all' || this.filterStartDate || this.filterEndDate);
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.filterStatus = 'all';
    this.filterOrganization = '';
    this.filterState = '';
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.onFilterChange();
  }

  // ============================================
  // PAGINATION
  // ============================================

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadReports();
    }
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  // ============================================
  // VIEW REPORT DETAIL
  // ============================================

  async viewReport(report: StateReport): Promise<void> {
    try {
      this.selectedReport = await this.stateReportService.getReport(report.id);
      this.detailModalVisible = true;
      this.openModal('detailModal');
    } catch (error: any) {
      console.error('Failed to load report details:', error);
      this.errorMessage = 'Failed to load report details';
    }
  }

  closeDetailModal(): void {
    this.detailModalVisible = false;
    this.selectedReport = null;
    this.closeModal('detailModal');
  }

  openReportUrl(): void {
    // Use downloadUrl (pre-signed S3 URL) if available, fall back to reportUrl
    const url = this.selectedReport?.downloadUrl || this.selectedReport?.reportUrl;
    if (url) {
      openExternal(url);
    }
  }

  // ============================================
  // DELETE ACTION
  // ============================================

  confirmDelete(report: StateReport): void {
    this.reportToDelete = report;
    this.deleteModalVisible = true;
    this.openModal('deleteModal');
  }

  cancelDelete(): void {
    this.reportToDelete = null;
    this.deleteModalVisible = false;
    this.closeModal('deleteModal');
  }

  async executeDelete(): Promise<void> {
    if (!this.reportToDelete) return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      await this.stateReportService.deleteReport(this.reportToDelete.id);
      this.successMessage = 'Report deleted successfully';
      this.cancelDelete();
      await this.loadReports();
      await this.loadQueueStats();
      this.clearSuccessMessage();
    } catch (error: any) {
      console.error('Delete error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to delete report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // REGENERATE ACTION
  // ============================================

  confirmRegenerate(report: StateReport): void {
    this.reportToRegenerate = report;
    this.regenerateModalVisible = true;
    this.openModal('regenerateModal');
  }

  cancelRegenerate(): void {
    this.reportToRegenerate = null;
    this.regenerateModalVisible = false;
    this.closeModal('regenerateModal');
  }

  async executeRegenerate(): Promise<void> {
    if (!this.reportToRegenerate) return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      await this.stateReportService.regenerateReport(this.reportToRegenerate.id);
      this.successMessage = 'Report regeneration started';
      this.cancelRegenerate();
      await this.loadReports();
      await this.loadQueueStats();
      this.clearSuccessMessage();
    } catch (error: any) {
      console.error('Regenerate error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to regenerate report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // RETRY ACTION
  // ============================================

  async retryReport(report: StateReport): Promise<void> {
    if (report.status !== 'failed') return;

    this.actionLoading = true;
    this.errorMessage = '';

    try {
      await this.stateReportService.retryReport(report.id);
      this.successMessage = 'Report retry started';
      await this.loadReports();
      await this.loadQueueStats();
      this.clearSuccessMessage();
    } catch (error: any) {
      console.error('Retry error:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to retry report';
    } finally {
      this.actionLoading = false;
    }
  }

  // ============================================
  // MODAL HELPERS
  // ============================================

  private openModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = new (window as any).bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  private closeModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      const modal = (window as any).bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  private clearSuccessMessage(): void {
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  // ============================================
  // FORMATTING HELPERS
  // ============================================

  formatDate(value: Date | string | null | undefined): string {
    if (!value) return '-';
    const dateString = value instanceof Date ? value.toISOString() : value;
    return this.parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
    if (!startDate || !endDate) return '-';
    const start = this.parseLocalDate(startDate);
    const end = this.parseLocalDate(endDate);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  }

  /**
   * Parse a date string at LOCAL midnight to avoid the UTC-shift off-by-one
   * that hits date-only strings like "2026-05-06". Falls back to default
   * parsing for full timestamps.
   */
  private parseLocalDate(dateString: string): Date {
    const m = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateString);
  }

  formatTimestamp(timestamp: number | null | undefined): string {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusClass(status: StateReportStatus | null | undefined): string {
    switch (status) {
      case 'draft': return 'bg-secondary';
      case 'generating': return 'bg-info';
      case 'ready': return 'bg-success';
      case 'submitted': return 'bg-primary';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getStatusLabel(status: StateReportStatus | null | undefined): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status ?? '';
    }
  }

  getStatusIcon(status: StateReportStatus | null | undefined): string {
    switch (status) {
      case 'draft': return 'bi-pencil';
      case 'generating': return 'bi-hourglass-split';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-circle';
      default: return 'bi-circle';
    }
  }

  getOrganizationName(organizationId: string | null | undefined): string {
    const org = this.organizations.find(o => o.id === organizationId);
    return org?.name || organizationId || '';
  }

  getStateName(stateCode: string | null | undefined): string {
    const state = US_STATES.find(s => s.value === stateCode);
    return state?.label || stateCode || '';
  }

  getReportTypeLabel(reportType: string | null | undefined): string {
    switch (reportType) {
      case 'all': return 'All Types';
      case 'pesticide': return 'Pesticide';
      case 'fertilizer': return 'Fertilizer';
      case 'seeding': return 'Seeding';
      case 'other': return 'Other';
      default: return reportType ?? '';
    }
  }

  canDelete(report: StateReport): boolean {
    return report.status !== 'generating';
  }

  canRetry(report: StateReport): boolean {
    return report.status === 'failed';
  }

  readonly Math = Math;
}
